import type { ActionTree, ActionContext, Commit } from 'vuex'
import type { RootState } from '@/store'
import type {
  DevicesState,
  LatestStudy,
  LatestTelemetryData,
  UpdateDeviceDetailsData,
  UpdateStudyDetailsData,
  SetDevicePayload,
  UpdateMetadataPayload
} from './types'
import type {
  StdTelemetry,
  CurrentReadings,
  StdState,
  AveragePM,
  ChangeOverTime
} from '@/types/devices'
import type { DeviceCoords, FormattedMeasurement } from '@/services/device-service/devices'
import type {
  RSSIResult,
  ConnectionTypeResult,
  EoLResult,
  FirmwareResult,
  MqttHostResult,
  LastStateResult,
  StudyIdResult
} from '@/services/device-service/state'
import type { DeviceDetails } from '@/helpers/devices/stateHelper'
import type { UserCalibration } from '@/services/device-calibration'
import {
  getDeviceDetails,
  getLatestTelemetry,
  getTelemetryAverages,
  getPercentChanged,
  getLatestCurrentReadings
} from '@/services/device-metadata'
import {
  getRSSI,
  getConnectionType,
  getEoL,
  getFirmwareVersion,
  getMqttHost,
  getConnectedSmartBridgeSerial,
  getLastState,
  getAllDevices,
  getUserDevices,
  getTelemTxType,
  getIsConnected,
  getBatteryCharging,
  getBatteryInstalled,
  getBatteryLow,
  getBatteryRemaining,
  getErrorState,
  getStudyId
} from '@/services/device-service'
import { activeLastDay } from '@/services/device-status'
import { getDeviceStatusFormattedResponse, getFormattedSensorData } from '@/store/devices/helper'
import { getDeviceDetailsFromState } from '@/helpers/devices/stateHelper'
import { getAllTelemetryStatisticsReadingsObject } from '@/helpers/devices/telemetryHelper'
import { getStatisticsReadingsFromState } from '@/helpers/studies/statisticsHelper'
import { DEVICE_MUTATIONS, DEVICE_ACTIONS, DEVICE_GETTERS } from './types'

interface IDeviceActions extends ActionTree<DevicesState, RootState> {
  [DEVICE_ACTIONS.SET_ALL_DEVICES](context: ActionContext<DevicesState, RootState>): Promise<void>
  [DEVICE_ACTIONS.SET_ALL_USER_DEVICES](
    context: ActionContext<DevicesState, RootState>
  ): Promise<void>
  [DEVICE_ACTIONS.SET_DEVICE](
    context: ActionContext<DevicesState, RootState>,
    device: SetDevicePayload
  ): void
  [DEVICE_ACTIONS.UPDATE_DEVICE_COORDS](
    context: ActionContext<DevicesState, RootState>,
    data: { deviceId: string; coords: DeviceCoords }
  ): void
  [DEVICE_ACTIONS.UPDATE_SENSOR_READINGS](
    context: ActionContext<DevicesState, RootState>,
    data: { deviceId: string; measurements: Record<string, FormattedMeasurement> }
  ): void
  [DEVICE_ACTIONS.UPDATE_RSSI](
    context: ActionContext<DevicesState, RootState>,
    data: RSSIResult
  ): void
  [DEVICE_ACTIONS.UPDATE_CONNECTION_TYPE](
    context: ActionContext<DevicesState, RootState>,
    data: ConnectionTypeResult
  ): void
  [DEVICE_ACTIONS.UPDATE_EOL](
    context: ActionContext<DevicesState, RootState>,
    data: EoLResult
  ): void
  [DEVICE_ACTIONS.UPDATE_USER_CALS](
    context: ActionContext<DevicesState, RootState>,
    data: { deviceId: string; userCals: UserCalibration[] }
  ): void
  [DEVICE_ACTIONS.UPDATE_FIRMWARE](
    context: ActionContext<DevicesState, RootState>,
    data: FirmwareResult
  ): void
  [DEVICE_ACTIONS.UPDATE_MQTT_HOST](
    context: ActionContext<DevicesState, RootState>,
    data: MqttHostResult
  ): void
  [DEVICE_ACTIONS.UPDATE_AVERAGES](
    context: ActionContext<DevicesState, RootState>,
    data: { deviceId: string; averages: AveragePM }
  ): void
  [DEVICE_ACTIONS.UPDATE_PERCENT_CHANGED](
    context: ActionContext<DevicesState, RootState>,
    data: { deviceId: string; percentChanged: ChangeOverTime }
  ): void
  [DEVICE_ACTIONS.UPDATE_IS_PUBLIC](
    context: ActionContext<DevicesState, RootState>,
    data: { deviceId: string; isPublic: boolean }
  ): void
  [DEVICE_ACTIONS.UPDATE_PRIVACY_ZONE](
    context: ActionContext<DevicesState, RootState>,
    data: { deviceId: string; privacyZone: unknown }
  ): void
  [DEVICE_ACTIONS.UPDATE_LAST_TELEMETRY](
    context: ActionContext<DevicesState, RootState>,
    data: { deviceId: string; timestamp: string }
  ): void
  [DEVICE_ACTIONS.UPDATE_LAST_STATE](
    context: ActionContext<DevicesState, RootState>,
    data: LastStateResult
  ): void
  [DEVICE_ACTIONS.UPDATE_LAST_TELEMETRY_STUDY_ID](
    context: ActionContext<DevicesState, RootState>,
    data: StudyIdResult
  ): void
  [DEVICE_ACTIONS.UPDATE_DEVICE_STATUS](
    context: ActionContext<DevicesState, RootState>,
    data: { deviceId: string; deviceStatus: string[] }
  ): void
  [DEVICE_ACTIONS.UPDATE_SENSOR_STATUS](
    context: ActionContext<DevicesState, RootState>,
    data: { deviceId: string; sensorStatus: string[] }
  ): void
  [DEVICE_ACTIONS.UPDATE_METADATA](
    context: ActionContext<DevicesState, RootState>,
    data: UpdateMetadataPayload
  ): void
  [DEVICE_ACTIONS.DELETE_DEVICE_BY_ID](
    context: ActionContext<DevicesState, RootState>,
    id: string
  ): void
  [DEVICE_ACTIONS.GET_TELEMETRY_AVERAGES_AND_UPDATE](
    context: ActionContext<DevicesState, RootState>,
    deviceId: string
  ): Promise<void>
  [DEVICE_ACTIONS.GET_PERCENT_CHANGED_AND_UPDATE](
    context: ActionContext<DevicesState, RootState>,
    deviceId: string
  ): Promise<void>
  [DEVICE_ACTIONS.GET_LATEST_TELEMETRY_AND_UPDATE](
    context: ActionContext<DevicesState, RootState>,
    deviceId: string
  ): Promise<void>
  [DEVICE_ACTIONS.GET_LATEST_CURRENT_READINGS_AND_UPDATE](
    context: ActionContext<DevicesState, RootState>,
    deviceId: string
  ): Promise<void>
  [DEVICE_ACTIONS.UPDATE_LATEST_TELEMETRY_FROM_STREAM](
    context: ActionContext<DevicesState, RootState>,
    data: StdTelemetry
  ): Promise<void>
  [DEVICE_ACTIONS.UPDATE_CURRENT_READINGS](
    context: ActionContext<DevicesState, RootState>,
    data: CurrentReadings
  ): Promise<void>
  [DEVICE_ACTIONS.GET_DEVICE_DETAILS_AND_UPDATE](
    context: ActionContext<DevicesState, RootState>,
    payload: { deviceId: string }
  ): Promise<void>
  [DEVICE_ACTIONS.UPDATE_DEVICE_STATE](
    context: ActionContext<DevicesState, RootState>,
    data: StdState
  ): Promise<void>
}

const actions: IDeviceActions = {
  // setAllDevices updates all devices data in Vuex, including latest telemetry.
  async [DEVICE_ACTIONS.SET_ALL_DEVICES]({ commit, getters }) {
    const data = await getAllDevices()

    commit(DEVICE_MUTATIONS.SET_ALL_DEVICES, data.devices)
    commit(DEVICE_MUTATIONS.SET_DEVICES, data.devices)
    commit(DEVICE_MUTATIONS.SET_SUBSCRIPTION_METADATA, data.activation)
    commit(DEVICE_MUTATIONS.SET_IOT_CORE_DATA, data.iotCoreData)
    commit(DEVICE_MUTATIONS.DELETE_REMOVED_DEVICES, data.devices)
    for (const device of data.state) {
      const { deviceId, state } = device

      const rssi = getRSSI(deviceId, state)
      const connectionType = getConnectionType(deviceId, state)
      const eol = getEoL(deviceId, state)
      const firmwareVersion = getFirmwareVersion(deviceId, state)
      const mqttHost = getMqttHost(deviceId, state)
      const connectedSmartBridgeSerial = getConnectedSmartBridgeSerial(deviceId, state)
      const lastState = getLastState(deviceId, state)
      const telemTxType = getTelemTxType(deviceId, state)
      const isConnected = getIsConnected(
        deviceId,
        state,
        getters[DEVICE_GETTERS.GET_LAST_COMMUNICATION_TIME_BY_DEVICE_ID](deviceId)
      )
      const batteryCharging = getBatteryCharging(deviceId, state)
      const batteryInstalled = getBatteryInstalled(deviceId, state)
      const batteryLow = getBatteryLow(deviceId, state)
      const batteryRemaining = getBatteryRemaining(deviceId, state)
      const lastTelemetry = getters[DEVICE_GETTERS.GET_LAST_CONTACT_BY_DEVICE_ID](deviceId)
      const isTelemetry24HrsActive = activeLastDay(lastTelemetry)
      const errorState = getErrorState(deviceId, state)
      const studyId = getStudyId(deviceId, state)

      commit(DEVICE_MUTATIONS.UPDATE_RSSI, rssi)
      commit(DEVICE_MUTATIONS.UPDATE_CONNECTION_TYPE, connectionType)
      commit(DEVICE_MUTATIONS.UPDATE_EOL, eol)
      commit(DEVICE_MUTATIONS.UPDATE_FIRMWARE, firmwareVersion)
      commit(DEVICE_MUTATIONS.UPDATE_MQTT_HOST, mqttHost)
      commit(DEVICE_MUTATIONS.UPDATE_CONNECTED_SMART_BRIDGE_SERIAL, connectedSmartBridgeSerial)
      commit(DEVICE_MUTATIONS.UPDATE_LAST_STATE, lastState)
      commit(DEVICE_MUTATIONS.UPDATE_TELEM_TX_TYPE, telemTxType)
      commit(DEVICE_MUTATIONS.UPDATE_IS_CONNECTED, isConnected)
      commit(DEVICE_MUTATIONS.UPDATE_IS_TELEMETRY_24HRS_ACTIVE, {
        deviceId,
        isTelemetry24HrsActive
      })
      commit(DEVICE_MUTATIONS.UPDATE_BATTERY_CHARGING, batteryCharging)
      commit(DEVICE_MUTATIONS.UPDATE_BATTERY_INSTALLED, batteryInstalled)
      commit(DEVICE_MUTATIONS.UPDATE_BATTERY_LOW, batteryLow)
      commit(DEVICE_MUTATIONS.UPDATE_BATTERY_REMAINING, batteryRemaining)
      commit(DEVICE_MUTATIONS.UPDATE_ERROR_STATE, errorState)
      commit(DEVICE_MUTATIONS.UPDATE_LAST_TELEMETRY_STUDY_ID, studyId)
    }
  },
  async [DEVICE_ACTIONS.SET_ALL_USER_DEVICES]({ commit }) {
    const { devices, activations } = await getUserDevices()
    commit(DEVICE_MUTATIONS.SET_ALL_USER_DEVICES, devices)
    commit(DEVICE_MUTATIONS.SET_USER_DEVICES, devices)
    commit(DEVICE_MUTATIONS.SET_SUBSCRIPTION_METADATA, activations)
  },
  [DEVICE_ACTIONS.SET_DEVICE]({ commit }, device) {
    commit(DEVICE_MUTATIONS.SET_DEVICE, device)
  },
  [DEVICE_ACTIONS.UPDATE_DEVICE_COORDS]({ commit }, data) {
    commit(DEVICE_MUTATIONS.UPDATE_COORDS, data)
  },
  [DEVICE_ACTIONS.UPDATE_SENSOR_READINGS]({ commit }, data) {
    commit(DEVICE_MUTATIONS.UPDATE_SENSOR_READINGS, data)
  },
  [DEVICE_ACTIONS.UPDATE_RSSI]({ commit }, data) {
    commit(DEVICE_MUTATIONS.UPDATE_RSSI, data)
  },
  [DEVICE_ACTIONS.UPDATE_CONNECTION_TYPE]({ commit }, data) {
    commit(DEVICE_MUTATIONS.UPDATE_CONNECTION_TYPE, data)
  },
  [DEVICE_ACTIONS.UPDATE_EOL]({ commit }, data) {
    commit(DEVICE_MUTATIONS.UPDATE_EOL, data)
  },
  [DEVICE_ACTIONS.UPDATE_USER_CALS]({ commit }, data) {
    commit(DEVICE_MUTATIONS.UPDATE_USER_CALS, data)
  },
  [DEVICE_ACTIONS.UPDATE_FIRMWARE]({ commit }, data) {
    commit(DEVICE_MUTATIONS.UPDATE_FIRMWARE, data)
  },
  [DEVICE_ACTIONS.UPDATE_MQTT_HOST]({ commit }, data) {
    commit(DEVICE_MUTATIONS.UPDATE_MQTT_HOST, data)
  },
  [DEVICE_ACTIONS.UPDATE_AVERAGES]({ commit }, data) {
    commit(DEVICE_MUTATIONS.UPDATE_AVERAGES, data)
  },
  [DEVICE_ACTIONS.UPDATE_PERCENT_CHANGED]({ commit }, data) {
    commit(DEVICE_MUTATIONS.UPDATE_PERCENT_CHANGED, data)
  },
  [DEVICE_ACTIONS.UPDATE_IS_PUBLIC]({ commit }, data) {
    commit(DEVICE_MUTATIONS.UPDATE_PUBLIC, data)
  },
  [DEVICE_ACTIONS.UPDATE_PRIVACY_ZONE]({ commit }, data) {
    commit(DEVICE_MUTATIONS.UPDATE_PRIVACY_ZONE, data)
  },
  [DEVICE_ACTIONS.UPDATE_LAST_TELEMETRY]({ commit }, data) {
    commit(DEVICE_MUTATIONS.UPDATE_LAST_TELEMETRY, data)
  },
  [DEVICE_ACTIONS.UPDATE_LAST_STATE]({ commit }, data) {
    commit(DEVICE_MUTATIONS.UPDATE_LAST_STATE, data)
  },
  [DEVICE_ACTIONS.UPDATE_LAST_TELEMETRY_STUDY_ID]({ commit }, data) {
    commit(DEVICE_MUTATIONS.UPDATE_LAST_TELEMETRY_STUDY_ID, data)
  },
  [DEVICE_ACTIONS.UPDATE_DEVICE_STATUS]({ commit }, data) {
    commit(DEVICE_MUTATIONS.UPDATE_DEVICE_STATUS, data)
  },
  [DEVICE_ACTIONS.UPDATE_SENSOR_STATUS]({ commit }, data) {
    commit(DEVICE_MUTATIONS.UPDATE_SENSOR_STATUS, data)
  },
  [DEVICE_ACTIONS.UPDATE_METADATA]({ commit }, data) {
    commit(DEVICE_MUTATIONS.UPDATE_INDOOR, data)
    commit(DEVICE_MUTATIONS.UPDATE_NAME, data)
    commit(DEVICE_MUTATIONS.UPDATE_LOCATION_NAME, data)
    commit(DEVICE_MUTATIONS.UPDATE_MODEL, data)
    commit(DEVICE_MUTATIONS.UPDATE_SUBMODEL, data)
    commit(DEVICE_MUTATIONS.UPDATE_SERIAL, data)
    commit(DEVICE_MUTATIONS.UPDATE_PUBLIC, data)
    commit(DEVICE_MUTATIONS.UPDATE_SHARED, data)
  },
  [DEVICE_ACTIONS.DELETE_DEVICE_BY_ID]({ commit }, id) {
    commit(DEVICE_MUTATIONS.DELETE_DEVICE_BY_ID, id)
  },
  async [DEVICE_ACTIONS.GET_TELEMETRY_AVERAGES_AND_UPDATE]({ commit }, deviceId) {
    const averages = await getTelemetryAverages(deviceId)
    commit(DEVICE_MUTATIONS.UPDATE_AVERAGES, { deviceId, averages })
  },
  async [DEVICE_ACTIONS.GET_PERCENT_CHANGED_AND_UPDATE]({ commit }, deviceId) {
    const percentChanged = await getPercentChanged(deviceId)
    commit(DEVICE_MUTATIONS.UPDATE_PERCENT_CHANGED, { deviceId, percentChanged })
  },
  async [DEVICE_ACTIONS.GET_LATEST_TELEMETRY_AND_UPDATE]({ commit }, deviceId) {
    const telemetry = await getLatestTelemetry(deviceId)
    updateLatestTelemetry(commit, { deviceId, ...telemetry })
  },
  async [DEVICE_ACTIONS.GET_LATEST_CURRENT_READINGS_AND_UPDATE]({ dispatch }, deviceId) {
    const currentReadings = await getLatestCurrentReadings(deviceId)
    const data = { deviceId, ...currentReadings }
    await dispatch(DEVICE_ACTIONS.UPDATE_CURRENT_READINGS, data)
  },
  async [DEVICE_ACTIONS.UPDATE_LATEST_TELEMETRY_FROM_STREAM]({ commit }, data) {
    // convert the raw telemetry data to the format expected by Vuex
    const telemetry = {
      readings: data,
      timestamp: data.timestamp,
      lastTelemetryStudyId: data.study_id,
      deviceId: data.cloud_device_id
    }
    // update the latest telemetry in Vuex
    updateLatestTelemetry(commit, telemetry)
  },
  async [DEVICE_ACTIONS.UPDATE_CURRENT_READINGS]({ commit }, data) {
    if (!data) return

    const latestTelemetry = data?.latest_device_telemetry
    const deviceId = data?.cloud_device_id || latestTelemetry?.cloud_device_id

    const telemetry = {
      deviceId,
      readings: latestTelemetry,
      timestamp: latestTelemetry?.timestamp,
      lastTelemetryStudyId: latestTelemetry?.study_id
    }

    // Clear outdated telemetry readings when a new study has been started
    // This prevents displaying stale readings that were collected before a new study begins
    if (latestTelemetry?.timestamp < data.latest_device_study?.start_time) {
      telemetry.readings = null
    }

    const deviceState = data.latest_device_state ?? ({} as StdState)
    const studyState = data.latest_study_state ?? ({} as StdState)

    updateLatestTelemetry(commit, telemetry)

    // this expects a deviceDetails object, which is derived from a V1 or V2 device state
    updateDeviceDetails(commit, {
      deviceId,
      deviceState: getDeviceDetailsFromState(deviceState),
      studyState: getDeviceDetailsFromState(studyState)
    })

    // Update study details when either:
    // A new study has started (study start time is after the last telemetry timestamp)
    // OR
    // The telemetry is associated with the current study (matching study IDs)
    if (
      data?.latest_device_study?.start_time > latestTelemetry?.timestamp ||
      latestTelemetry?.study_id
    ) {
      updateStudyDetails(commit, {
        deviceId,
        latest_device_study: data.latest_device_study,
        latest_study_state: data.latest_study_state,
        latest_study_telemetry: data.latest_study_telemetry
      })
    } else {
      // If there is no latest_device_study, reset the latest study state
      commit(DEVICE_MUTATIONS.UPDATE_LATEST_STUDY, { deviceId, latestStudy: null })
    }
  },
  async [DEVICE_ACTIONS.GET_DEVICE_DETAILS_AND_UPDATE]({ commit }, { deviceId }) {
    const deviceDetails = await getDeviceDetails(deviceId)
    updateDeviceDetails(commit, { deviceId, deviceState: deviceDetails })
  },
  // When updating device state from the streaming endpoint,
  // only update the telemTxType and uploadStatus to not interfere with latest study state data
  async [DEVICE_ACTIONS.UPDATE_DEVICE_STATE]({ commit }, data) {
    const deviceDetails = getDeviceDetailsFromState(data)
    const deviceId = data.cloud_device_id
    const telemTxType = deviceDetails?.telemTxType
    const uploadStatus = deviceDetails?.uploadStatus

    commit(DEVICE_MUTATIONS.UPDATE_TELEM_TX_TYPE, { deviceId, telemTxType })
    commit(DEVICE_MUTATIONS.UPDATE_UPLOAD_STATUS, { deviceId, uploadStatus })
  }
}

// ---------------------------------------------------------------------------
// Module-level helpers (not exported — internal to actions)
// ---------------------------------------------------------------------------

function updateLatestTelemetry(commit: Commit, data: LatestTelemetryData): void {
  const { readings, timestamp, lastTelemetryStudyId, deviceId } = data
  let measurements: Record<string, unknown> = {}
  let sensorStatus: string[] | null = null

  if (readings?.sensors) {
    ;({ measurements, sensorStatus } = getFormattedSensorData(readings.sensors))
  }

  const isTelemetry24HrsActive = activeLastDay(timestamp)

  commit(DEVICE_MUTATIONS.UPDATE_SENSOR_READINGS, { deviceId, measurements })
  commit(DEVICE_MUTATIONS.UPDATE_LAST_TELEMETRY, { deviceId, timestamp })
  commit(DEVICE_MUTATIONS.UPDATE_IS_TELEMETRY_24HRS_ACTIVE, { deviceId, isTelemetry24HrsActive })
  commit(DEVICE_MUTATIONS.UPDATE_LAST_TELEMETRY_STUDY_ID, { deviceId, lastTelemetryStudyId })
  commit(DEVICE_MUTATIONS.UPDATE_DEVICE_STATUS, {
    deviceId,
    deviceStatus: getDeviceStatusFormattedResponse(readings)
  })
  commit(DEVICE_MUTATIONS.UPDATE_SENSOR_STATUS, { deviceId, sensorStatus })
}

function updateDeviceDetails(commit: Commit, data: UpdateDeviceDetailsData): void {
  // both states are a "deviceDetails" object derived from a V1 or V2 device state
  const { deviceId, studyState } = data
  // make sure deviceState is an object, even if it's empty
  // this is to avoid errors when destructuring properties from deviceState
  const deviceState = data.deviceState || {}
  const {
    name,
    locationName,
    indoor,
    isPublic,
    rssi,
    connectionType,
    spsEoL,
    firmware,
    timestamp,
    model,
    serial,
    zeroAppliedAt,
    zeroState,
    telemTxType,
    batteryCharging,
    batteryInstalled,
    batteryLow,
    batteryRemaining,
    uploadStatus,
    isConnected,
    mqttHost
  } = deviceState

  // Determine which state has the more recent timestamp
  const studyTimestamp = studyState?.timestamp ? new Date(studyState.timestamp) : null
  const deviceTimestamp = deviceState?.timestamp ? new Date(deviceState.timestamp) : null

  // primaryState will be the state with the most recent timestamp
  // fallbackState will be the other state
  let primaryState: DeviceDetails | undefined
  let fallbackState: DeviceDetails | undefined

  if (studyTimestamp && deviceTimestamp) {
    // Both timestamps exist, compare them
    if (studyTimestamp >= deviceTimestamp) {
      primaryState = studyState
      fallbackState = deviceState
    } else {
      primaryState = deviceState
      fallbackState = studyState
    }
  } else {
    // Only one timestamp exists, or neither exists
    primaryState = studyTimestamp ? studyState : deviceState
    fallbackState = studyTimestamp ? deviceState : studyState
  }

  // Get values from primary state first, then fallback state, then null
  const errorState = primaryState?.errorState ?? fallbackState?.errorState ?? null
  const alarms = primaryState?.alarms ?? fallbackState?.alarms ?? null
  const userCal = primaryState?.userCal ?? fallbackState?.userCal ?? null
  const flowCalibrationFactor =
    primaryState?.flowCalibrationFactor ?? fallbackState?.flowCalibrationFactor ?? null
  const calibrationDate = primaryState?.calibrationDate ?? fallbackState?.calibrationDate ?? null

  commit(DEVICE_MUTATIONS.UPDATE_INDOOR, { deviceId, indoor })
  commit(DEVICE_MUTATIONS.UPDATE_NAME, { deviceId, name })
  commit(DEVICE_MUTATIONS.UPDATE_LOCATION_NAME, { deviceId, locationName })
  commit(DEVICE_MUTATIONS.UPDATE_PUBLIC, { deviceId, isPublic })

  // device state
  commit(DEVICE_MUTATIONS.UPDATE_RSSI, { deviceId, rssi })
  commit(DEVICE_MUTATIONS.UPDATE_CONNECTION_TYPE, { deviceId, connectionType })
  commit(DEVICE_MUTATIONS.UPDATE_EOL, { deviceId, spsEoL })
  commit(DEVICE_MUTATIONS.UPDATE_FIRMWARE, { deviceId, firmware })
  commit(DEVICE_MUTATIONS.UPDATE_MQTT_HOST, { deviceId, mqttHost })
  commit(DEVICE_MUTATIONS.UPDATE_LAST_STATE, { deviceId, timestamp })
  commit(DEVICE_MUTATIONS.UPDATE_MODEL, { deviceId, model })
  commit(DEVICE_MUTATIONS.UPDATE_SERIAL, { deviceId, serial })
  commit(DEVICE_MUTATIONS.UPDATE_ZERO_APPLIED_AT, { deviceId, zeroAppliedAt })
  commit(DEVICE_MUTATIONS.UPDATE_ZERO_STATE, { deviceId, zeroState })
  commit(DEVICE_MUTATIONS.UPDATE_CALIBRATION_DATE, { deviceId, calibrationDate })
  commit(DEVICE_MUTATIONS.UPDATE_FLOW_CALIBRATION_FACTOR, { deviceId, flowCalibrationFactor })
  commit(DEVICE_MUTATIONS.UPDATE_USER_CAL, { deviceId, userCal })
  commit(DEVICE_MUTATIONS.UPDATE_ERROR_STATE, { deviceId, errorState })
  commit(DEVICE_MUTATIONS.UPDATE_TELEM_TX_TYPE, { deviceId, telemTxType })
  commit(DEVICE_MUTATIONS.UPDATE_BATTERY_CHARGING, { deviceId, batteryCharging })
  commit(DEVICE_MUTATIONS.UPDATE_BATTERY_INSTALLED, { deviceId, batteryInstalled })
  commit(DEVICE_MUTATIONS.UPDATE_BATTERY_LOW, { deviceId, batteryLow })
  commit(DEVICE_MUTATIONS.UPDATE_BATTERY_REMAINING, { deviceId, batteryRemaining })
  commit(DEVICE_MUTATIONS.UPDATE_UPLOAD_STATUS, { deviceId, uploadStatus })
  commit(DEVICE_MUTATIONS.UPDATE_IS_CONNECTED, { deviceId, isConnected })
  commit(DEVICE_MUTATIONS.UPDATE_ALARMS, { deviceId, alarms })
}

function updateStudyDetails(commit: Commit, data: UpdateStudyDetailsData): void {
  const { deviceId, latest_device_study, latest_study_state, latest_study_telemetry } = data
  // get min_timestamp, max_timestamp, duration, upload_complete, and termination_reason from latest_device_study
  const latestStudy: LatestStudy = {
    minTimestamp: latest_device_study?.min_timestamp,
    maxTimestamp: latest_device_study?.max_timestamp,
    duration: latest_device_study?.duration,
    samplingInterval: latest_device_study?.sampling_interval,
    uploadComplete: latest_device_study?.upload_complete,
    terminationReason: latest_device_study?.termination_reason,
    startTime: latest_device_study?.start_time
  }

  if (latest_study_telemetry) {
    // Live studies have statistics in telemetry, while offline studies in state.
    const measurementStatistics =
      getAllTelemetryStatisticsReadingsObject(latest_study_telemetry) ||
      getStatisticsReadingsFromState(latest_study_state, latest_study_telemetry.timestamp)
    latestStudy.measurementStatistics = measurementStatistics
  }

  commit(DEVICE_MUTATIONS.UPDATE_LATEST_STUDY, { deviceId, latestStudy })
}

export default actions
